import { useContext } from "react";
import { type ImageSourcePropType } from "react-native";
import { XStack, YStack } from "tamagui";

import Step2Image from "@/assets/images/landing_page/general_care/how-it-work-02.jpg";
import Step1Image from "@/assets/images/landing_page/general_care/how-it-works-01.jpg";
import Step3Image from "@/assets/images/landing_page/general_care/how-it-works-03.png";
import { Image } from "@/components";
import { LayoutContext } from "@/components/LayoutProvider";
import { Text } from "@/components/Typography";
import LandingGradientBackground from "./LandingGradientBackground";

type StepItem = {
  number: string;
  title: string;
  description: string;
  image: ImageSourcePropType;
};

const DEFAULT_STEPS: StepItem[] = [
  {
    number: "01",
    title: "Describe what's going on",
    description:
      "Ask a health question or talk about how you're feeling with our health AI.",
    image: Step1Image,
  },
  {
    number: "02",
    title: "Connect with a clinician",
    description:
      "A board-certified clinician reviews your case and tailors your care.",
    image: Step2Image,
  },
  {
    number: "03",
    title: "Get the treatment you need",
    description:
      "Prescriptions and labs ordered for you. Ongoing follow up support.",
    image: Step3Image,
  },
];

function StepCard({ step }: { step: StepItem }) {
  return (
    <YStack
      width="100%"
      $lg={{ flex: 1, flexBasis: 0, width: "auto" }}
      backgroundColor="$backgroundFill"
      borderRadius="$200"
      overflow="hidden"
      gap="$300"
    >
      <Image source={step.image} width="100%" height={160} objectFit="cover" />

      <YStack gap="$75" padding="$300" paddingTop={0}>
        <Text size="$xxl" color="$textTertiary">
          {step.number}
        </Text>
        <Text size="$lg">{step.title}</Text>
        <Text size="$sm" color="$textSecondary">
          {step.description}
        </Text>
      </YStack>
    </YStack>
  );
}

type HowItWorksProps = {
  title?: string;
  subtitle?: string;
  steps?: StepItem[];
};

export default function HowItWorks({
  title = "Healthcare made easy",
  subtitle = "No insurance required. Only pay for medication and labs.",
  steps = DEFAULT_STEPS,
}: HowItWorksProps) {
  const { lg } = useContext(LayoutContext);

  return (
    <LandingGradientBackground>
      <YStack
        maxWidth="$landingPageConstraint"
        alignSelf="center"
        paddingHorizontal="$400"
        paddingVertical="$200"
        gap="$600"
        alignItems="center"
        width="100%"
      >
        <YStack alignItems="center" gap="$100">
          <Text size="$xxl" textAlign="center">
            {title}
          </Text>
          <Text size="$md" textAlign="center" color="$textSecondary">
            {subtitle}
          </Text>
        </YStack>

        <XStack
          gap="$300"
          alignItems="stretch"
          width="100%"
          flexDirection={lg ? "row" : "column"}
        >
          {steps.map((step) => (
            <StepCard key={step.number} step={step} />
          ))}
        </XStack>
      </YStack>
    </LandingGradientBackground>
  );
}
